import { groupProjectMedia } from '@functions/attachment'

import useAttachment from '@hooks/useAttachment'
import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ImageRequestOptions } from '@models/types'

import * as imageActions from '@redux/modules/image'

import type { EntityOptions } from '@redux/modules/module'

import type { AppDispatch } from '@redux/store'

type FetchCampaignImagesParams = {
  campaignId: number,
  dispatch: AppDispatch,
  requestOptions: ImageRequestOptions,
}

const fetchCampaignImages = (params: FetchCampaignImagesParams) => {
  const { campaignId, dispatch, requestOptions } = params
  const { loadImages: loadImagesFn } = imageActions

  const options = {
    ...requestOptions,
    imageable_id: campaignId,
    imageable_type: 'Campaign',
  }

  return dispatch(loadImagesFn(options))
}

type Options = {
  requestOptions?: EntityOptions,
}

const useImportProjectMedia = (campaignId: number, options: Options = {}) => {
  const dispatch = useDispatch()

  const { callbacks: { copyToCampaign } } = useAttachment()

  useReduxAction(
    'campaigns',
    'loadPendingMedia',
    {
      entityId: campaignId,
      ...options.requestOptions,

    },
    [campaignId],
    {
      dispatchAction: (action, requestOptions) => action(campaignId, requestOptions),
      shouldPerformFn: () => !!campaignId,
    },
  )

  const { attachments, orderItems } = useSelector(reduxState => reduxState.entities)

  const groupedProjectMedia = groupProjectMedia({ attachments, campaignId, orderItems })

  return {
    callbacks: {
      copyToCampaign: (orderItemId: number, attachmentIds: number[]) => copyToCampaign({ attachmentIds, orderItemId }),
      fetchCampaignImages: (requestOptions: ImageRequestOptions) => (
        fetchCampaignImages({ campaignId, dispatch, requestOptions })
      ),
    },
    groupedProjectMedia,
    hasGroupedProjectMedia: !!groupedProjectMedia.length,
  }
}

export type UseImportProjectMediaPayload = ReturnType<typeof useImportProjectMedia>

export default useImportProjectMedia
